import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
// @ts-ignore - no type declarations for this module
import { default as oneLight } from "react-syntax-highlighter/dist/esm/styles/prism/one-light.js";
import { useTheme } from "../hooks/useTheme.js";
import {
  Copy,
  Check,
  Download,
  FileText,
  Image as ImageIcon,
  Music,
  Video,
  File,
  ExternalLink,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// Detectar tipo de arquivo pela extensão
function getFileType(fileName: string): "image" | "audio" | "video" | "code" | "document" | "other" {
  const ext = fileName.split(".").pop()?.toLowerCase() || "";
  if (["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "ico"].includes(ext)) return "image";
  if (["mp3", "wav", "ogg", "m4a", "flac", "aac", "webm"].includes(ext)) return "audio";
  if (["mp4", "avi", "mov", "mkv", "webm"].includes(ext)) return "video";
  if (["ts", "tsx", "js", "jsx", "py", "rs", "go", "java", "c", "cpp", "h", "css", "scss", "html", "json", "yaml", "yml", "toml", "sql", "sh", "bash", "md", "mdx"].includes(ext)) return "code";
  if (["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv"].includes(ext)) return "document";
  return "other";
}

function getFileIcon(type: string) {
  switch (type) {
    case "image": return ImageIcon;
    case "audio": return Music;
    case "video": return Video;
    case "code": return FileText;
    case "document": return FileText;
    default: return File;
  }
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

// Botão de copiar código
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      onClick={handleCopy}
      className="absolute top-2 right-2 p-1.5 rounded-md bg-background/80 hover:bg-background border border-border text-muted-foreground hover:text-foreground transition-all opacity-0 group-hover:opacity-100"
      title="Copiar código"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

// Componente de preview de imagem
function ImagePreview({ url, alt }: { url: string; alt?: string }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="my-3">
      <img
        src={url}
        alt={alt || "Imagem"}
        className={`rounded-lg border border-border cursor-pointer transition-all ${
          expanded ? "max-w-full" : "max-w-[400px] max-h-[300px] object-cover"
        }`}
        onClick={() => setExpanded(!expanded)}
      />
      {!expanded && (
        <p className="text-[10px] text-muted-foreground mt-1">Clique para expandir</p>
      )}
    </div>
  );
}

// Componente de player de áudio
function AudioPlayer({ url, fileName }: { url: string; fileName: string }) {
  return (
    <div className="my-3 bg-background border border-border rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <Music className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium text-foreground">{fileName}</span>
      </div>
      <audio controls className="w-full h-10" preload="metadata">
        <source src={url} />
        Seu navegador não suporta áudio.
      </audio>
    </div>
  );
}

// Componente de player de vídeo
function VideoPlayer({ url, fileName }: { url: string; fileName: string }) {
  return (
    <div className="my-3 bg-background border border-border rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <Video className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium text-foreground">{fileName}</span>
      </div>
      <video controls className="w-full max-h-[400px]" preload="metadata">
        <source src={url} />
        Seu navegador não suporta vídeo.
      </video>
    </div>
  );
}

// Componente de card de arquivo para download
function FileCard({ fileName, url, sizeBytes }: { fileName: string; url: string; sizeBytes?: number }) {
  const fileType = getFileType(fileName);
  const Icon = getFileIcon(fileType);

  // Para imagens, mostrar preview inline
  if (fileType === "image") {
    return <ImagePreview url={url} alt={fileName} />;
  }

  // Para áudio, mostrar player
  if (fileType === "audio") {
    return <AudioPlayer url={url} fileName={fileName} />;
  }

  // Para vídeo, mostrar player
  if (fileType === "video") {
    return <VideoPlayer url={url} fileName={fileName} />;
  }

  // Para outros arquivos, mostrar card de download
  return (
    <div className="my-2 flex items-center gap-3 bg-background border border-border rounded-lg p-3 hover:border-primary/30 transition-colors group">
      <div className="p-2 rounded-lg bg-primary/10">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{fileName}</p>
        {sizeBytes && (
          <p className="text-xs text-muted-foreground">{formatBytes(sizeBytes)}</p>
        )}
      </div>
      <div className="flex items-center gap-1">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          title="Abrir"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
        <a
          href={url}
          download={fileName}
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          title="Download"
        >
          <Download className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}

// Componente principal de renderização de conteúdo rico (Markdown)
export function RichMarkdown({ content }: { content: string }) {
  const { theme } = useTheme();
  const isLight = theme === "light";
  return (
    <div className={[
      "prose prose-sm max-w-none break-words",
      isLight ? "" : "prose-invert",
      "prose-headings:text-foreground prose-headings:font-semibold prose-headings:tracking-tight",
      "prose-h1:text-[17px] prose-h1:mt-6 prose-h1:mb-3 prose-h1:pb-2.5 prose-h1:border-b prose-h1:border-border/60",
      "prose-h2:text-[15px] prose-h2:mt-5 prose-h2:mb-2.5",
      "prose-h3:text-[13.5px] prose-h3:mt-4 prose-h3:mb-2 prose-h3:text-foreground/90",
      "prose-p:text-foreground/85 prose-p:leading-[1.8] prose-p:my-3 prose-p:text-[13.5px]",
      "prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-a:font-medium",
      "prose-strong:text-foreground prose-strong:font-semibold",
      "prose-em:text-foreground/75 prose-em:italic",
      "prose-code:text-primary prose-code:bg-primary/8 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:text-xs prose-code:font-mono prose-code:before:content-none prose-code:after:content-none prose-code:border prose-code:border-primary/10",
      "prose-ul:my-3 prose-ul:pl-1 prose-ol:my-3 prose-ol:pl-1",
      "prose-li:my-1.5 prose-li:text-foreground/85 prose-li:text-[13.5px] prose-li:leading-[1.75] prose-li:marker:text-primary/50",
      "prose-blockquote:border-l-[3px] prose-blockquote:border-primary/30 prose-blockquote:bg-primary/[0.03] prose-blockquote:rounded-r-xl prose-blockquote:py-3 prose-blockquote:px-4 prose-blockquote:text-foreground/75 prose-blockquote:my-4 prose-blockquote:text-[13px] prose-blockquote:not-italic prose-blockquote:leading-relaxed",
      "prose-table:border-border prose-th:text-foreground prose-td:text-foreground/85",
      "prose-hr:border-border/50 prose-hr:my-5",
      "prose-img:rounded-xl prose-img:border prose-img:border-border prose-img:shadow-sm",
    ].filter(Boolean).join(" ")}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={(url) => {
          // Permitir data: URLs (base64 images, audio, etc)
          if (url.startsWith('data:')) return url;
          // Permitir blob: URLs
          if (url.startsWith('blob:')) return url;
          // Permitir URLs normais (http, https)
          if (url.startsWith('http://') || url.startsWith('https://')) return url;
          // Permitir URLs relativas
          if (url.startsWith('/') || url.startsWith('./')) return url;
          return url;
        }}
        components={{
          // Blocos de código com syntax highlighting
          code({ className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "");
            const codeString = String(children).replace(/\n$/, "");

            if (match) {
              return (
                <div className="relative group my-3 not-prose">
                  <div className={"flex items-center justify-between px-3 py-1.5 " + (isLight ? "bg-[#fafafa]" : "bg-[#1e1e2e]") + " rounded-t-lg border border-b-0 border-border"}>
                    <span className="text-[10px] text-muted-foreground font-mono uppercase">{match[1]}</span>
                  </div>
                  <CopyButton text={codeString} />
                  <SyntaxHighlighter
                    style={isLight ? oneLight : oneDark}
                    language={match[1]}
                    PreTag="div"
                    customStyle={{
                      margin: 0,
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      borderBottomLeftRadius: "0.5rem",
                      borderBottomRightRadius: "0.5rem",
                      fontSize: "0.8rem",
                      border: "1px solid var(--border)",
                      borderTop: "none",
                    }}
                  >
                    {codeString}
                  </SyntaxHighlighter>
                </div>
              );
            }

            // Inline code
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },

          // Imagens com preview
          img({ src, alt }) {
            if (!src) return null;
            return <ImagePreview url={src} alt={alt || undefined} />;
          },

          // Links com ícone externo
          a({ href, children }) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                {children}
                <ExternalLink className="w-3 h-3 inline shrink-0" />
              </a>
            );
          },

          // Tabelas estilizadas
          table({ children }) {
            return (
              <div className="my-3 overflow-x-auto rounded-lg border border-border not-prose">
                <table className="w-full text-sm">{children}</table>
              </div>
            );
          },
          thead({ children }) {
            return <thead className="bg-secondary">{children}</thead>;
          },
          th({ children }) {
            return (
              <th className="px-3 py-2 text-left text-xs font-medium text-foreground border-b border-border">
                {children}
              </th>
            );
          },
          td({ children }) {
            return (
              <td className="px-3 py-2 text-xs text-foreground/90 border-b border-border">
                {children}
              </td>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

// Componente para renderizar attachments de uma task
export function TaskAttachments({
  attachments,
}: {
  attachments: Array<{ fileName: string; url: string; sizeBytes: number }>;
}) {
  const [expanded, setExpanded] = useState(attachments.length <= 3);

  if (!attachments || attachments.length === 0) return null;

  const visibleAttachments = expanded ? attachments : attachments.slice(0, 3);

  return (
    <div className="mt-3">
      <div className="flex items-center gap-2 mb-2">
        <File className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">
          {attachments.length} arquivo{attachments.length !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="space-y-1">
        {visibleAttachments.map((att, i) => (
          <FileCard key={i} fileName={att.fileName} url={att.url} sizeBytes={att.sizeBytes} />
        ))}
      </div>
      {attachments.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs text-primary hover:underline mt-2"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3 h-3" />
              Mostrar menos
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              Ver todos ({attachments.length - 3} mais)
            </>
          )}
        </button>
      )}
    </div>
  );
}

export { FileCard };
